import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import ArrayStore from "devextreme/data/array_store";
import { environment } from "src/environments/environment";
import { JobDetail, JobGroup, JobObservableConfig } from "./batch-reports-grid-types";
import { Observable, Subject, concat, forkJoin, takeUntil, timer } from "rxjs";
import { Utility } from "src/app/common/service/utility";

@Injectable({
  providedIn: 'root'
})
export class BatchReportsService {

  jobDetails: JobDetail[];
  baseurl = environment.apiBaseUrl;
  JobGroupArrayStore: ArrayStore;
  JobDetailsArrayStore: ArrayStore;

  constructor(protected http: HttpClient, private utility: Utility,) {

  }

  getUserJobInfo(reportType: string) {
    return this.http.get<JobGroup[]>(this.baseurl + `BatchReports/GetUserJobInfo?reportType=${reportType}`);
  }


  setJobGroupArrayStore(jobGroupStore: ArrayStore) {
    this.JobGroupArrayStore = jobGroupStore;
  }

  getJobDetails() {
    return this.jobDetails;
  }

  setJobDetails(jobDetails: JobDetail[]) {
    this.jobDetails = jobDetails;
    this.JobDetailsArrayStore = new ArrayStore({
      data: this.jobDetails,
      key: 'jobID',
    });
  }

  getJobDetailsArrayStore() {
    return this.JobDetailsArrayStore;
  }

  submitJob(reportId: number, reportRefId: string, requestBody: any) {

    return this.http.post<any>(this.baseurl +
      `BatchReports/SubmitJob?reportId=${reportId}&reportRefId=${reportRefId}`, requestBody);

  }

  deleteSiteAsync(siteId): Observable<number> {
    return this.http.delete<number>(environment.apiBaseUrl + 'BatchReports/deletesiterecord/' + siteId);
  }

  getJobOutput(jobID: string) {

    //Create an observable that will get the job output from backend.
    //The data will be received as {blockId, totalBlocks, jobOutput }
    //If totalBlocks > 1, then we will call backend API again to fetch each of those blocks.
    //Then we decompress/decode the jobOutput which can result in a JSON array (most cases) or a JSON Object (ex. LS Suspect Map)
    //If it is array, the contents for each block will be appended to a master array.
    //Finally we return either the combined master JSON array or the JSON Object to the Observer.
    let jobOutputBlockObserver = new Observable<any>((observer) => {

      let jobOutputAsJsonArray = new Array<any>(); //master JSON array
      let jobOutputAsObject = new Object(); //JSON Object

      let decompress_and_append = (response) => { //function to be used below.
        var decompressedData = this.utility.decompressTextData(response.jobOutput as string);
        var output = JSON.parse(decompressedData);

        if (typeof output == 'string') {
          output = JSON.parse(output);
        }

        if (Array.isArray(output)) {
          jobOutputAsJsonArray.push.apply(jobOutputAsJsonArray, output);
          return 'array';
        } else {
          jobOutputAsObject = output;
          return 'object';
        }
      }

      this.http.get<any>(this.baseurl + `BatchReports/GetJobOutputBlock?jobID=${jobID}`)
        .subscribe({ //Get the first block
          next: async response => {

            let returnType = await decompress_and_append(response);

            let remainingBlocksObservables = Array<Observable<any>>();

            for (var i = 2; i <= response.totalBlocks; i++) { //If there are more blocks, create observable array
              remainingBlocksObservables
                .push(this.http.get<any>(this.baseurl + `BatchReports/GetJobOutputBlock?jobID=${jobID}&blockId=${i}`));
            }

            //Get output for remaining blocks. Concat will get them sequentially one after the other, so that order is maintained.
            concat(...remainingBlocksObservables).subscribe({
              next: async response => {
                returnType = await decompress_and_append(response);
              },
              error: errorResponse => {
                observer.error({ status: 500, error: { message: `Get job output block failed; JobID = ${jobID}; Status =  ${errorResponse.status}` } });
                observer.complete();
              },
              complete: () => { //Finally push the output to observer
                (returnType == 'array') ? observer.next(jobOutputAsJsonArray) : observer.next(jobOutputAsObject);
                observer.complete();
              }
            });
          },
          error: errorResponse => {
            observer.error({ status: 500, error: { message: `Get job output block failed; JobID = ${jobID}, blockId = 1; Status =  ${errorResponse.status}` } });
            observer.complete();
          }
        });

    });

    return jobOutputBlockObserver;
  }

  getJobStatusAndOutput(jobID: string) {
    return this.http.get<any>(this.baseurl + `BatchReports/GetJobStatusAndOutput?jobID=${jobID}`, { observe: 'response' });
  }

  getAPIBody(jobID: string) {
    return this.http.get<any>(environment.apiBaseUrl + `BatchReports/GetAPIBody?jobID=${jobID}`);
  }


  refreshJobStatus(reportType: string, reportRefID: string) {
    this.http.get<JobGroup[]>(this.baseurl + `BatchReports/GetUserJobInfo?reportType=${reportType}&reportRefId=${reportRefID}`)
      .subscribe({
        next: jobGroupsData => {

          let jobStatusMap = new Map<string, number>();

          jobGroupsData.forEach((jobGroup, i) => {

            jobStatusMap.clear();

            //Using the jobs[] array in repsponse, update the job status in the Arraystore.
            //This will get reflected in UI immediately in the details grid.
            jobGroup.jobs.forEach(job => {

              let count = jobStatusMap.get(job.status);
              count ? jobStatusMap.set(job.status, ++count) : jobStatusMap.set(job.status, 1);

              this.JobDetailsArrayStore.push([{
                type: 'update',
                key: job.jobID,
                data: {
                  status: job.status,
                  lastUpdateDateTime: new Date(job.lastUpdateDateTime + 'Z')
                },
              }]);
            });

            jobStatusMap.set('total', jobGroup.jobs.length);
            let statusSummary = this.getStatusSummary(jobStatusMap);

            //Update the status summary using job status data accumulated in previous steps
            ////This will get reflected in UI immediately in the main grid row.
            this.JobGroupArrayStore.push([{
              type: 'update',
              key: jobGroup.reportRefID,
              data: {
                statusSummary: statusSummary,

              },
            }]);

          });
        }
      });
  }

  getStatusSummary(statusMap: Map<string, number>) {

    let total = statusMap.get('total');
    let completed = statusMap.get('Completed') ?? 0;
    let submitted = statusMap.get('Submitted') ?? 0;
    let inProgress = statusMap.get('InProgress') ?? 0;
    let failed = statusMap.get('Failed') ?? 0;

    let statusSummary = (total == completed) ? 'Completed' : `${completed}/${total} Completed`;

    if (submitted > 0) {
      statusSummary = statusSummary + `; ${submitted} submitted`
    }

    if (inProgress > 0) {
      statusSummary = statusSummary + `; ${inProgress} in progress`
    }

    if (failed > 0) {
      statusSummary = statusSummary + `; ${failed} failed`
    }

    return statusSummary;
  }

  //Returns an observable that monitors the given jobId's execution status. 
  //If job completes, it emits an event with the job output. If job fails or max wait time is reached, it emits an error event.
  //Pass the optional 'config' object to customize status check intervals and max retries. Otherwise it will use some defaults.    
  getJobObservable(jobID: string, config?: JobObservableConfig): Observable<any> {

    //Note - It is highly recommended that observers should use the first() operator when subscribing to this observable. This is because during network/system slowness,
    //if the retry interval is not sufficient enough, then the first status check call will be in-progress, but the second call goes out as well.
    //Both calls will bring back data, resulting in two event emits. For example, refer to src\app\reports\crm-data-by-zip-territory\crm-report-datagrid\crm-report-datagrid.component.ts

    let final_config = Object.assign(
      {
        statusCheckAft: 30000, //milliseconds after which the job status will be checked first time.
        retryInterval: 10000, //milliseconds to wait for each subsequent status check
        maxRetries: 10 //maximum number of status checks (including the very first check)
      } as JobObservableConfig, config);

    let jobObserver = new Observable((observer) => {

      const stopTimer$ = new Subject<boolean>();
      let retries = 0, maxRetries = final_config.maxRetries;

      //This timer will fire the first event after {statusCheckAft} ms and then every {retryInterval} ms.
      timer(final_config.statusCheckAft, final_config.retryInterval)
        .pipe(takeUntil(stopTimer$))
        .subscribe(() => {

          this.getJobStatusAndOutput(jobID).subscribe({
            next: response => {

              let jobStatus = response.headers.get('X-Job-Status');

              //If job has completed, emit event to the observer with response body (which is the job ouput); stop status checks.
              if (jobStatus == 'Completed') {
                this.getJobOutput(jobID).subscribe(observer);
                stopTimer$.next(true);
              } else if (jobStatus == 'Failed') { //if job failed, emit error event to observer; stop status checks
                observer.error({ status: 500, error: { message: `Job execution failed; JobID = ${jobID} ` } });
                stopTimer$.next(true);
              }

            },
            error: errorResponse => { //if status check failed, emit error event to observer
              observer.error({ status: 500, error: { message: `Job status check failed; JobID = ${jobID}; Status =  ${errorResponse.status}` } });
              stopTimer$.next(true);
            }
          });

          retries++;

          if (retries >= maxRetries) { //if max retries reached, emit error event to observer; stop further status checks.
            observer.error({ status: 500, error: { message: `Wait limit reached; Job did not complete; JobID = ${jobID} ` } });
            stopTimer$.next(true);
          }
        });
    });

    return jobObserver;
  }

}